/** @module liff/helper LIFF関連の共通処理を記述するモジュールファイル */

import liff from '@line/liff'
import * as Sentry from '@sentry/browser'
import AgentAPI from './agent_api'

/**LIFF ID */
export const liffId = document.getElementById('liff_id')?.getAttribute('data');

/**
 * 現在開いているページがLIFFアプリかどうかチェックする
 */
export function isLiffApp(): boolean {
  // LIFF IDの有無でLIFFアプリかどうかを判定
  return Boolean(liffId);
}

/** 
 * ログイン必要エラー
 * * このエラーをスローする場合、自動的にログイン画面にリダイレクトします。
 * */
export class LoginRequiredError extends Error {
  constructor() {
    super('Login required');
    this.name = 'LoginRequiredError';
  }
}

/**
 * LIFFアプリを初期化
 * @async
 * @throws {LoginRequiredError} ログイン必要エラー
 * */
export async function initLiff(): Promise<void> {
  try {
    // エラーテストモードの場合はここでエラーを発生させる
    if(isErrorTestMode()) {
      throw { code: 'ERROR_TEST_MODE', message: 'Error test mode' };
    }

    // LIFFを初期化
    await liff.init({ liffId });
  }
  catch(error) {
    // 初期化失敗時
    console.error(error);

    // LIFF SDKのエラー発生を通知(レスポンスは待たない)
    AgentAPI.post('/api/v1/liff/report_liff_error', {
      liff_error: error,
      pathname: location.pathname
    });
    Sentry.captureException(error);

    // 再スロー
    throw error;
  }

  // ログインしていない、またはアクセストークンが取得できない場合はログインしてエラー終了
  if(!liff.isLoggedIn() || !liff.getAccessToken()) {
    // 現在のページが一次リダイレクト先ではない場合のみ、ログイン後のリダイレクト先を設定
    // ※https化していないローカル開発環境における、[URL検証機能](https://developers.line.biz/ja/docs/liff/developing-liff-apps/#url-validation)
    // で弾かれるのを防ぐため
    let redirectUri = null;
    if(window.location.pathname !== '/liff') {
      redirectUri = window.location.toString();
    }

    // ログイン
    liff.login({ redirectUri });
    throw new LoginRequiredError;
  }
}

/** 求職者情報 */
export interface Person {
  /** シリアル(求職者ID) */
  serial: string;
  /** 都道府県コード */
  prefecture: number;
  /** 雇用形態コード */
  employment: number;
}

/**
 * LINEアカウントから求職者情報を取得する
 */
export async function getPerson(): Promise<Person> {
  const response = await AgentAPI.get<Person>(
    `/api/v1/liff/person?line_access_token=${liff.getAccessToken()}`
  );

  return response.data;
}

/**
 * (内部関数)エラーテストモードかどうかチェックする
 * * ※クエリ文字列に`liff_error_test=1`を含む場合はエラーテストモード
 */
function isErrorTestMode(): boolean {
  return location.search.includes('liff_error_test=1');
}
